import { useEffect, useState } from 'preact/hooks';
import type { Theme } from '../styles/theme.js';
import { MessageList } from './MessageList.js';
import { InputComposer, type ComposedAttachment } from './InputComposer.js';
import { StatusBanner } from './StatusBanner.js';
import { ContactForm } from './ContactForm.js';
import { STRINGS } from '../strings.js';
import type {
  Message,
  Status,
  EscalationStatus,
  AvailabilityState,
  AiMode,
} from '../state/store.js';
import { isSendBlocked, shouldOfferSoftInvite } from '../lib/availability.js';

type Props = {
  messages: Message[];
  status: Status;
  escalation: EscalationStatus;
  aiThinking: boolean;
  availability: AvailabilityState;
  availabilityMessage: string;
  aiMode: AiMode;
  emailCaptured: boolean;
  showSoftInvite: boolean;
  hasPendingMessage: boolean;
  privacyUrl: string;
  onSend: (content: string, attachments: ComposedAttachment[]) => void;
  /** Téléverse une photo et renvoie son id + son URL signée (cf. App.tsx). */
  onUpload: (file: File) => Promise<ComposedAttachment>;
  /** Erreur d'envoi remontée par le serveur (ex. photo expirée). */
  sendError: string | null;
  onDismissSendError: () => void;
  onClose: () => void;
  onRetry: () => void;
  onContactSubmit: (email: string, name: string) => void;
  onSoftInviteDismiss: () => void;
  // Quick win §9 : relance manuelle d'un message visiteur non-envoyé.
  onRetrySend?: ((localId: string) => void) | undefined;
  theme: Theme;
};

const isMobile = typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches;

export function ChatWindow({
  messages,
  status,
  escalation,
  aiThinking,
  availability,
  availabilityMessage,
  aiMode,
  emailCaptured,
  showSoftInvite,
  hasPendingMessage,
  privacyUrl,
  onSend,
  onUpload,
  sendError,
  onDismissSendError,
  onClose,
  onRetry,
  onContactSubmit,
  onSoftInviteDismiss,
  onRetrySend,
  theme,
}: Props) {
  // Panneau plein écran mobile : iOS ne réduit pas le layout viewport quand
  // le clavier s'ouvre, il fait défiler la page — un `position: fixed` +
  // `inset: 0` seul se retrouve alors partiellement caché par le clavier,
  // en laissant apparaître le fond du site en dessous (bug constaté). On
  // (a) verrouille le scroll du document pendant que le panneau est ouvert,
  // et (b) recale le panneau sur `window.visualViewport` (hauteur + offset)
  // à chaque redimensionnement, avec repli `100dvh` si l'API est absente.
  const [viewport, setViewport] = useState<{ height: number; top: number } | null>(null);
  const [resizeTick, setResizeTick] = useState(0);

  useEffect(() => {
    if (!isMobile) return;

    const scrollY = window.scrollY;
    const html = document.documentElement.style;
    const body = document.body.style;
    const prev = {
      htmlOverflow: html.overflow,
      bodyOverflow: body.overflow,
      bodyPosition: body.position,
      bodyWidth: body.width,
      bodyTop: body.top,
    };
    html.overflow = 'hidden';
    body.overflow = 'hidden';
    body.position = 'fixed';
    body.width = '100%';
    body.top = `-${scrollY}px`;

    const vv = window.visualViewport;
    function updateViewport(): void {
      if (!vv) return;
      setViewport({ height: vv.height, top: vv.offsetTop });
      setResizeTick((t) => t + 1);
    }
    if (vv) {
      updateViewport();
      vv.addEventListener('resize', updateViewport);
      vv.addEventListener('scroll', updateViewport);
    }

    return () => {
      html.overflow = prev.htmlOverflow;
      body.overflow = prev.bodyOverflow;
      body.position = prev.bodyPosition;
      body.width = prev.bodyWidth;
      body.top = prev.bodyTop;
      window.scrollTo(0, scrollY);
      if (vv) {
        vv.removeEventListener('resize', updateViewport);
        vv.removeEventListener('scroll', updateViewport);
      }
    };
    // Ce panneau est démonté à la fermeture (cf. App.tsx : rendu conditionné
    // par isOpen) : cet effet ne tourne donc que pendant qu'il est ouvert,
    // et le nettoyage ci-dessus s'exécute bien à la fermeture/démontage.
  }, []);

  const baseStyle: Record<string, string> = isMobile
    ? {
        position: 'fixed',
        left: '0',
        right: '0',
        top: viewport ? `${viewport.top}px` : '0',
        height: viewport ? `${viewport.height}px` : '100dvh',
        backgroundColor: theme.bgColor,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        paddingBottom: 'env(safe-area-inset-bottom)',
        zIndex: '999999',
      }
    : {
        position: 'fixed',
        bottom: '94px',
        right: '20px',
        width: '380px',
        height: '580px',
        backgroundColor: theme.bgColor,
        boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
        borderRadius: '12px',
        display: 'flex',
        flexDirection: 'column',
        zIndex: '999999',
        overflow: 'hidden',
      };

  const blocked = isSendBlocked({ availability, emailCaptured });
  const softInviteEligible = shouldOfferSoftInvite({ availability, emailCaptured });

  return (
    <div style={baseStyle}>
      <header
        style={{
          padding: '12px 16px',
          backgroundColor: theme.brandColor,
          color: '#fff',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          fontSize: '15px',
          fontWeight: 600,
        }}
      >
        <span>{STRINGS.windowTitle}</span>
        <button
          type="button"
          onClick={onClose}
          aria-label={STRINGS.buttonClose}
          style={{
            background: 'transparent',
            border: 'none',
            color: '#fff',
            fontSize: '20px',
            cursor: 'pointer',
            lineHeight: 1,
          }}
        >
          ×
        </button>
      </header>
      <StatusBanner
        status={status}
        escalation={escalation}
        availability={availability}
        availabilityMessage={availabilityMessage}
        onRetry={onRetry}
      />
      <MessageList
        messages={messages}
        aiThinking={aiThinking}
        aiMode={aiMode}
        availability={availability}
        theme={theme}
        scrollTrigger={resizeTick}
        onRetry={onRetrySend}
      />
      {blocked && (
        <ContactForm
          variant="blocking"
          theme={theme}
          onSubmit={onContactSubmit}
          showPendingNote={hasPendingMessage}
        />
      )}
      {!blocked && softInviteEligible && showSoftInvite && (
        <ContactForm
          variant="soft"
          theme={theme}
          onSubmit={onContactSubmit}
          onDismiss={onSoftInviteDismiss}
        />
      )}
      {sendError && (
        <div
          role="alert"
          onClick={onDismissSendError}
          style={{
            padding: '8px 16px 0',
            fontSize: '12px',
            color: '#b3261e',
            cursor: 'pointer',
          }}
        >
          {sendError}
        </div>
      )}
      <InputComposer
        onSend={onSend}
        onUpload={onUpload}
        disabled={status !== 'connected'}
        theme={theme}
      />
      <div
        style={{ padding: '4px 16px 10px', fontSize: '11px', color: '#999', textAlign: 'center' }}
      >
        {STRINGS.privacyPrefix}
        <a href={privacyUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#999' }}>
          {STRINGS.privacyLinkText}
        </a>
        .
      </div>
    </div>
  );
}
